import { Activity, Ellipsis, GitMerge, Layers } from '@orgus/ui/icons';
import { NavLink } from 'react-router';
import type { ComponentType, ReactNode } from 'react';
import { Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, IconButton, Tooltip, cn, plural, useNow } from '@orgus/ui';
import { useLanding } from './api.ts';
import { needsAttention } from './model.ts';

interface Tab {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

/** Pipelines has three views; the rail keeps one entry (it stays at 9 items). */
export const PIPELINE_TABS: readonly Tab[] = [
  { to: '/pipelines', label: 'Pipelines', icon: Activity, end: true },
  { to: '/pipelines/landing', label: 'Landing queue', icon: GitMerge },
  { to: '/pipelines/environments', label: 'Environments', icon: Layers },
];

/**
 * The one header row of Pipelines: the underline tabs, one link per view so every view has its
 * own address, and the view's own controls at the right (its picker, status and actions). On a
 * phone the controls wrap under the tabs, and the tabs fit the width: the tab you are on keeps its
 * label, the others show their icon (the label stays their accessible name), so no tab
 * is ever pushed off the edge.
 */
export function PipelinesTabs({ children, fit }: { children?: ReactNode; /** The controls are small enough to stay on the tab row on a phone (a menu alone). */ fit?: boolean }) {
  const s = useLanding().state;
  const now = useNow(60_000);
  const inFlight = s ? s.queues.reduce((n, q) => n + q.order.length + (q.activeId ? 1 : 0), 0) : undefined;
  // The same list as the Needs you strip and the Overview: parked, and failed in the last day.
  const needs = s ? needsAttention(s.landings, now).length : 0;
  return (
    <div className="mb-6 flex min-w-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-border-subtle max-sm:pb-2">
    <nav aria-label="Pipelines views" className="flex h-12 min-w-0 max-w-full items-stretch gap-6 overflow-x-auto overflow-y-hidden whitespace-nowrap max-sm:gap-4">
      {PIPELINE_TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              'relative inline-flex shrink-0 items-center gap-2 border-b-2 border-transparent text-ui text-fg-subtle',
              'transition-colors duration-(--duration-fast) hover:text-fg-muted focus-visible:-outline-offset-2 [&_svg]:size-4',
              isActive && 'border-accent font-medium text-fg',
            )
          }
        >
          {({ isActive }) => (
            <>
              <t.icon />
              <span className={cn(!isActive && 'max-sm:sr-only')}>{t.label}</span>
              {t.to === '/pipelines/landing' && inFlight != null && inFlight > 0 && (
                <Tooltip content={`${plural(inFlight, 'landing')} queued or in progress`}>
                  <span className="text-caption text-fg-subtle num max-sm:hidden" aria-label={`${inFlight} in flight`}>
                    {inFlight}
                  </span>
                </Tooltip>
              )}
              {t.to === '/pipelines/landing' && needs > 0 && (
                <Badge tone="waiting" aria-label={`${needs} need you`}>
                  {needs} need{needs === 1 ? 's' : ''} you
                </Badge>
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
      {children != null && <div className={cn('ml-auto flex min-w-0 max-w-full flex-wrap items-center gap-2', !fit && 'max-sm:ml-0 max-sm:w-full')}>{children}</div>}
    </div>
  );
}

export interface TabMenuItem {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  danger?: boolean;
}

/**
 * The last control of every Pipelines tab's header row: one overflow menu, in the same place on
 * each tab, holding what is not needed at rest (settings, restart). Each item says what it does in
 * words, so no control in the row is an icon without a label.
 */
export function TabMenu({ label, items, header, disabled }: { label: string; items: readonly TabMenuItem[]; header?: ReactNode; disabled?: boolean }) {
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size="sm" label={label} icon={<Ellipsis />} disabled={disabled} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {header}
        {items.map((i) => (
          <DropdownMenuItem key={i.label} icon={i.icon} danger={i.danger} onSelect={i.onSelect}>
            {i.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
